import {type AppError, InvalidTokenError, type Result} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  type GenerateBodySchema,
  GenerateResponseSchema,
  GetResultResponseScema,
  QuestionsResponseSchema,
  ThemesResponseSchema,
} from '../entities/entrysheetBuilder/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

/**
 * POST /3.0/entrysheetBuilder/generate
 */
export type GenerateEntrySheetBodyType = z.infer<typeof GenerateBodySchema>;
export type GenerateEntrySheetArgs = {
  version?: string;
  cookies: Partial<Record<string, string>>;
  body: GenerateEntrySheetBodyType;
};

export const generateEntrySheet = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  body,
}: GenerateEntrySheetArgs) => {
  const request = async (accessToken: string) => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    return defaultClient.post(
      `/${version}/entrysheet-builder`,
      GenerateResponseSchema,
      body,
    );
  };

  const result = await sendApiRequest({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

export type ResultGenerateEntrySheet = Result<
  z.infer<typeof GenerateResponseSchema>,
  AppError
>;

export const useGenerateEntrySheet = ({
  version = DEFAULT_API_VERSION,
}: {
  version?: string;
}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation(async (body: GenerateEntrySheetBodyType) => {
    const cookies = parseCookies();

    const result = await generateEntrySheet({
      cookies,
      version,
      body,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

/**
 * GET /3.0/entrysheetBuilder/themes
 */
type GetThemasType = z.infer<typeof ThemesResponseSchema>;
export type ResultGetThemas = Result<GetThemasType, AppError>;

export const getThemes = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: {
  cookies: Partial<Record<string, string>>;
  version?: string;
}): Promise<ResultGetThemas> => {
  const request = async (
    accessToken?: string,
  ): Promise<Result<GetThemasType, AppError>> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(
      `/${version}/entrysheet-builder/themes`,
      {},
    );
    return defaultClient.get(path, ThemesResponseSchema);
  };

  const result = await sendApiRequest<GetThemasType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetThemasOptions = {
  config?: UseQueryOptions<ResultGetThemas>;
  version?: string;
};

export const useGetThemes = ({
  version = DEFAULT_API_VERSION,
  config,
}: UseGetThemasOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetThemas>({
    ...config,
    queryKey: ['getThemes'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getThemes({cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GET /3.0/entrysheetBuilder/questions/:themeId
 */
type GetQuestionsType = z.infer<typeof QuestionsResponseSchema>;
export type ResultGetQuestions = Result<GetQuestionsType, AppError>;

type GetQuestionsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  themeId: number;
};

export const getQuestions = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  themeId,
}: GetQuestionsArgs): Promise<ResultGetQuestions> => {
  const request = async (
    accessToken?: string,
  ): Promise<Result<GetQuestionsType, AppError>> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(
      `/${version}/entrysheet-builder/questions/${themeId}`,
      {},
    );
    return defaultClient.get(path, QuestionsResponseSchema);
  };

  const result = await sendApiRequest<GetQuestionsType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetQuestionsOptions = {
  version?: string;
  themeId: number;
  config?: UseQueryOptions<ResultGetQuestions>;
};

export const useGetQuestions = ({
  config,
  version = DEFAULT_API_VERSION,
  themeId,
}: UseGetQuestionsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetQuestions>({
    ...config,
    queryKey: ['getQuestions', themeId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getQuestions({cookies, version, themeId});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GET /3.0/entrysheetBuilder/result/:builderId
 */

type GetResultType = z.infer<typeof GetResultResponseScema>;
export type ResultGetResult = Result<GetResultType, AppError>;

const getResultByBuilderId = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  builderId,
}: {
  cookies: Partial<Record<string, string>>;
  version?: string;
  builderId: string;
}): Promise<ResultGetResult> => {
  const request = async (
    accessToken?: string,
  ): Promise<Result<GetResultType, AppError>> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    const path = createPathWithQuery(
      `/${version}/entrysheet-builder/result/${builderId}`,
      {},
    );
    return defaultClient.get(path, GetResultResponseScema);
  };

  const result = await sendApiRequest<GetResultType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetResultOptions = {
  version?: string;
  builderId: string;
  config?: UseQueryOptions<ResultGetResult>;
};

export const useGetResultByBuilderId = ({
  version = DEFAULT_API_VERSION,
  builderId,
  config,
}: UseGetResultOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGetResult>({
    ...config,
    queryKey: ['getResult', builderId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getResultByBuilderId({
        cookies,
        version,
        builderId,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
